import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { MenuPage } from '../../layouts';
import { PageTitle, Button, Text, Badge, Loader } from '../../components';
import { routes } from '../../routes/Routes';
import { ReactComponent as IconAdd } from '../../assets/img/icn-add.svg';
import { ReactComponent as IconDownload } from '../../assets/img/icn-download.svg';
import { formatDateTime, formatDate } from '../../utils/date';
import { makePost, makeGet } from '../../api/ajax';
import { composeApiUrl } from '../../api/composeApiUrl';
import { constants as fileConstants } from '../../redux/files';
import Pagination from '../Vehicles/containers/VehiclesTable/containers/Pagination';

import './sass/links-exported.scss';

const LinksExported = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 20;

  const { state } = useLocation();

  const fetchExportedLinks = () => {
    setIsLoading(true);
    makeGet('accessUrls/export').subscribe(({ response }) => {
      setResults(response);
      setIsLoading(false);
    });
  };

  const handleExport = () => {
    setExportError(null);
    setIsLoading(true);
    makePost('accessUrls/export', { startDate: startDate === '' ? null : startDate, endDate: endDate === '' ? null : endDate }).subscribe(
      () => {
        fetchExportedLinks();
      },
      async (err) => {
        const { status, response } = err;
        switch (status) {
          case 400:
            setExportError(response.error);
            break;
          default:
            setExportError('Failed to export links. Please try again.');
            break;
        }
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    fetchExportedLinks();
  }, []);

  useEffect(() => {
    if (!results || results.length === 0 || !results.some((item) => item.status === 0)) return;
    const interval = setInterval(() => fetchExportedLinks(), 10000);

    return () => clearInterval(interval);
  }, [results]);

  const handleFileDownload = (filterKey, fileEntityType) => {
    makeGet(`files/${filterKey}/token?entityType=${fileEntityType}`).subscribe(
      ({ response }) => {
        window.location = composeApiUrl(`files/${filterKey}/download?token=${response.token}`);
      },
      ({ response: { error } }) => console.error(error),
    );
  };

  const pageCount = Math.ceil(results.length / PAGE_SIZE);
  const pagedResults = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="links-exported">
      <MenuPage currentRoute={routes.vehicles}>
        <PageTitle>
          <PageTitle.Left headingText="Link usage reports" isLoading={isLoading} backUrl={state?.from || routes.vehicles}>
            {!isLoading && (
              <Badge className="ml-2">
                {results?.length || 0}
              </Badge>
            )}
          </PageTitle.Left>
          <PageTitle.Right text="left">
            <div className="d-flex align-items-end justify-content-end">
              <div className="form-group mb-0 mr-2">
                <label htmlFor="links-range-start" className="mb-0 mt-1">Start date</label>
                <input
                  id="links-range-start"
                  name="links-range-start"
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group mb-0 mr-2">
                <label htmlFor="links-range-start" className="mb-0 mt-1">End date</label>
                <input
                  id="links-range-end"
                  name="links-range-end"
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  max={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                />
              </div>
              <Button className="btn btn-primary" onClick={handleExport} testId="exportLinkBtn">
                <IconAdd />&nbsp;Create report
              </Button>
            </div>
          </PageTitle.Right>
        </PageTitle>

        {exportError && (
          <div className="row mb-3">
            <div className="col-sm-12">
              <Alert variant="danger">
                {exportError}
              </Alert>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-sm-12">
            {results?.length > 0 && (
              <div className="links-exported-table">
                <div className="links-exported-table-row">
                  <div className="row">
                    <div className="col-sm-3 export-text">
                      <Text size={1} weight="bold">Links created at</Text>
                    </div>
                    <div className="col-sm-3 export-text">
                      <Text size={1} weight="bold">Export created at</Text>
                    </div>
                    <div className="col-sm-3 export-text">
                      <Text size={1} weight="bold">Status</Text>
                    </div>
                    <div className="col-sm-3 export-download-text">
                      <Text size={1} weight="bold">CSV</Text>
                    </div>
                  </div>
                </div>
                {pagedResults.map((item) => (
                  <div className="links-exported-table-row" key={item.key}>
                    <div className="row">
                      <div className="col-sm-3 export-text">
                        <Text size={1}>{`${formatDate(item.startDate)} - ${formatDate(item.endDate)}`}</Text>
                      </div>
                      <div className="col-sm-3 export-text">
                        <Text size={1}>{formatDateTime(item.exportCreatedAt)}</Text>
                      </div>
                      <div className="col-sm-3 export-text">
                        {item.status === 0 && (<div className="row-loader-wrapper"><Loader size="small" /></div>)}
                        <Text size={1}>{item.status === 1 ? 'Completed' : 'In progress'}</Text>
                      </div>
                      <div className="col-sm-3 export-download-text">
                        <button
                          className="btn btn-link d-flex align-items-center"
                          type="button"
                          disabled={item.status === 0}
                          onClick={() => handleFileDownload(item.filterKey, fileConstants.FILE_ENTITY_TYPES.ACCESS_URL_EXPORT_JSON)}
                        >
                          <IconDownload className="mr-2" style={{ width: '1rem' }} />
                          <Text size={1} weight="bold" inline>Download</Text>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <Pagination
              resultCount={results.length}
              currentPage={currentPage}
              pageCount={pageCount}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </MenuPage>
    </div>
  );
};

export default LinksExported;
